// İSG uzmanı portalı — Panel A "İzinler — duruma göre" (S34-4, design review
// `S34-carbon-phase2-plan.md` §4 "Panel A", Carbon *proportional meter* şekli). 12 `PermitStatus`
// dört AÇIK-İŞ kovasına gruplanır (M12) — kova sınırları tasarım incelemesinin ÖRNEĞİ DEĞİL,
// görevin bağlı olduğu copy sheet'in (`S34-copy.md` §"Coordinator decisions") kararı:
// Hazırlanıyor = Draft+Revision · Taşeronda = Approved+AwaitingSupplierInfo · İncelemede =
// Submitted+AwaitingAiReview+DepartmentReview+AwaitingEntryPermit · Aktif = Active. Üç terminal
// statü (Rejected/Expired/Closed) çubukta YER ALMAZ — yalnız altta renksiz bir toplam satırı.
//
// **Erişilebilirlik: ayrı bir sr-only tablo YOK (Panel B'nin AKSİNE, bilinçli).** M12'nin
// "sr-only veri tablosu" gereksinimi 30 günlük çubuğu hedefler (30 barın metin karşılığı hiçbir
// yerde GÖRÜNMÜYOR); burada aynı bilgi ZATEN görünür bir metin lejantı (`swatch + etiket +
// sayı`) olarak var — ikinci, gizli bir kopya REDUNDANT olurdu. `<svg>` bu yüzden
// `aria-hidden="true"` (dekoratif — lejant tek gerçek kaynak).

import { type ExpertDashboardPermitStatus, cn } from '@isgportal/shared';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type BucketKey = 'preparing' | 'withContractor' | 'inReview' | 'active';

// `fillClass`/`swatchClass` TAM SINIF ADLARI olarak yazılır, `` `fill-${x}` `` gibi bir şablon
// dizgesi DEĞİL — Tailwind v4'ün içerik taraması kaynak METNİNİ okur (JS'i ÇALIŞTIRMAZ); parçalı
// bir şablon üretimi derlenmiş CSS'te sınıfı hiç YARATMAZDI (`Tag.tsx`'in `colors` sözlüğüyle AYNI
// kaçınma deseni).
const BUCKETS: {
  key: BucketKey;
  statuses: ExpertDashboardPermitStatus[];
  fillClass: string;
  swatchClass: string;
  /** Yalnız TAM OLARAK tek statüye eşlenen kova bir bağlantıdır (design review §4 "A legend
   * item is a link only when its bucket is exactly one status"). */
  linkStatus?: ExpertDashboardPermitStatus;
}[] = [
  {
    key: 'preparing',
    statuses: ['Draft', 'Revision'],
    fillClass: 'fill-dataviz-1',
    swatchClass: 'bg-dataviz-1',
  },
  {
    key: 'withContractor',
    statuses: ['Approved', 'AwaitingSupplierInfo'],
    fillClass: 'fill-dataviz-2',
    swatchClass: 'bg-dataviz-2',
  },
  {
    key: 'inReview',
    statuses: ['Submitted', 'AwaitingAiReview', 'DepartmentReview', 'AwaitingEntryPermit'],
    fillClass: 'fill-dataviz-3',
    swatchClass: 'bg-dataviz-3',
  },
  {
    key: 'active',
    // Owner review B1 item 3 — bu kova TAM OLARAK tek statüye eşlenir (`Active`), o statünün
    // `Tag`i her yerde yeşil (`getPermitStatusPresentation` → `tagColor: 'green'`, Carbon'da
    // `--color-success`); önceki `dataviz-4` (Magenta 70) eşleşmiyordu ("Aktif bucket is
    // magenta but the Aktif tag is green everywhere"). Diğer üç kova BİRDEN FAZLA statüyü
    // karıştırır (bazıları farklı `tagColor`), TEK bir "eşleşen" renk YOK — kategorik
    // `dataviz-*` paleti onlarda KALIR (bu bulgunun kapsamı yalnız Aktif).
    statuses: ['Active'],
    fillClass: 'fill-success',
    swatchClass: 'bg-success',
    linkStatus: 'Active',
  },
];

const TERMINAL_STATUSES: ExpertDashboardPermitStatus[] = ['Rejected', 'Expired', 'Closed'];

const BUCKET_LABEL_KEYS: Record<BucketKey, string> = {
  preparing: 'dashboard.chart.bucket.preparing',
  withContractor: 'dashboard.chart.bucket.withContractor',
  inReview: 'dashboard.chart.bucket.inReview',
  active: 'dashboard.chart.bucket.active',
};

/** SVG viewBox genişliği (birim) — 1px yerine 3 birimlik bir boşluk kullanılır (segmentler
 * arası, design review "1px --color-surface gap"); tipik render genişliğinde (~260-400px) 3/1000
 * ≈ 1px'e karşılık gelir, tam piksel eşitliği ARANMAZ (dekoratif çubuk, ölçüm hedefi değil). */
const VIEWBOX_WIDTH = 1000;
const GAP_UNITS = 3;

export function PermitsByStatusChart({
  permitsByStatus,
}: {
  permitsByStatus: Record<ExpertDashboardPermitStatus, number>;
}) {
  const { t } = useTranslation();

  const bucketCounts = BUCKETS.map((bucket) => ({
    ...bucket,
    count: bucket.statuses.reduce((sum, s) => sum + (permitsByStatus[s] ?? 0), 0),
  }));
  const total = bucketCounts.reduce((sum, b) => sum + b.count, 0);
  const terminalCount = TERMINAL_STATUSES.reduce((sum, s) => sum + (permitsByStatus[s] ?? 0), 0);

  const visibleBuckets = bucketCounts.filter((b) => b.count > 0);
  const gapCount = Math.max(0, visibleBuckets.length - 1);
  const availableWidth = VIEWBOX_WIDTH - gapCount * GAP_UNITS;

  let cursor = 0;
  const segments = visibleBuckets.map((bucket) => {
    const width = total > 0 ? (bucket.count / total) * availableWidth : 0;
    const x = cursor;
    cursor += width + GAP_UNITS;
    return { ...bucket, x, width };
  });

  return (
    <div className="flex flex-col gap-3 p-4">
      {total === 0 ? (
        <p className="py-6 text-center text-sm text-muted">
          {t('dashboard.chart.permitsByStatus.empty')}
        </p>
      ) : (
        <>
          <p className="text-sm text-muted">
            {t('dashboard.chart.permitsByStatus.total', { count: total })}
          </p>
          <svg
            viewBox={`0 0 ${VIEWBOX_WIDTH} 16`}
            preserveAspectRatio="none"
            className="h-4 w-full"
            aria-hidden="true"
          >
            <title>{t('dashboard.chart.permitsByStatus.title')}</title>
            {segments.map((s) => (
              <rect
                key={s.key}
                x={s.x}
                y={0}
                width={Math.max(0, s.width)}
                height={16}
                className={s.fillClass}
              />
            ))}
          </svg>
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {bucketCounts
              .filter((b) => b.count > 0)
              .map((bucket) => {
                const label = t(BUCKET_LABEL_KEYS[bucket.key]);
                const content = (
                  <>
                    <span
                      className={cn('h-3 w-3 shrink-0', bucket.swatchClass)}
                      aria-hidden="true"
                    />
                    <span className="text-xs text-fg">{label}</span>
                    <span className="tnum text-xs text-muted">{bucket.count}</span>
                  </>
                );
                return (
                  <li key={bucket.key} className="flex items-center gap-1.5">
                    {bucket.linkStatus ? (
                      <Link
                        to={`/permits?status=${bucket.linkStatus}`}
                        className="flex items-center gap-1.5 rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {content}
                      </Link>
                    ) : (
                      content
                    )}
                  </li>
                );
              })}
          </ul>
          {terminalCount > 0 ? (
            <p className="tnum text-xs text-muted">
              {t('dashboard.chart.permitsByStatus.terminal', { count: terminalCount })}
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
